import { useEffect } from "react";
import { EventIdPublic, EventSlug } from "@shared/schemas/branded-id.schemas";
import { FeatureKey } from "@shared/schemas/feature-key.schemas";
import {
  buildBaseDomainUrl,
  buildSubdomainUrl,
  stripEventPrefix,
} from "@shared/utils/subdomain-url.utils";
import { useIsFeatureEnabledForCurrentUser } from "@frontend/shared/features/feature-config/application/use-cases/is-feature-enabled-for-current-user.use-case";
import { applicationLogger } from "@frontend/shared/infrastructure/logger/application-logger.singleton";

export interface PreferredUrlRedirectResult {
  readonly shouldBlock: boolean;
}

export function usePreferredUrlRedirect(
  isEventSubdomain: boolean,
  eventIdPublic: EventIdPublic,
  eventSlug: EventSlug | null,
  rawIdPublic: string | null,
  eventIdParam: string | undefined,
): PreferredUrlRedirectResult {
  const { isEnabled, isLoading, error } = useIsFeatureEnabledForCurrentUser({
    featureKey: FeatureKey.EVENT_SUBDOMAIN,
    eventIdPublic,
    enabled: rawIdPublic !== null,
  });

  const dataReady = rawIdPublic !== null && !isLoading;
  const featureResolved = dataReady && error === null;

  useEffect(() => {
    if (!featureResolved) return;

    const pathname = window.location.pathname;
    const search = window.location.search;

    if (!isEventSubdomain && isEnabled) {
      try {
        const subdomainUrl = buildSubdomainUrl(
          window.location.origin,
          eventIdPublic,
          eventSlug,
        );
        const strippedPath = stripEventPrefix(
          pathname,
          eventIdPublic,
          eventSlug,
        );
        window.location.replace(`${subdomainUrl}${strippedPath}${search}`);
      } catch {
        applicationLogger.warn(
          "Failed to build subdomain URL for redirect — skipping",
          "usePreferredUrlRedirect",
          [{ eventIdPublic: EventIdPublic.unwrap(eventIdPublic) }],
        );
      }
      return;
    }

    if (isEventSubdomain && !isEnabled) {
      const baseDomainUrl = buildBaseDomainUrl(window.location.host);
      if (baseDomainUrl !== null) {
        const preferredId =
          eventSlug !== null
            ? EventSlug.unwrap(eventSlug)
            : EventIdPublic.unwrap(eventIdPublic);
        window.location.replace(
          `${baseDomainUrl}/e/${preferredId}${pathname}${search}`,
        );
      } else {
        applicationLogger.warn(
          "Cannot redirect from subdomain — unknown base domain",
          "usePreferredUrlRedirect",
          [{ host: window.location.host }],
        );
      }
      return;
    }

    if (!isEventSubdomain && eventSlug !== null && eventIdParam !== undefined) {
      const slugStr = EventSlug.unwrap(eventSlug);
      if (eventIdParam !== slugStr) {
        const stripped = stripEventPrefix(pathname, eventIdPublic, eventSlug);
        if (stripped !== pathname) {
          window.location.replace(`/e/${slugStr}${stripped}${search}`);
        }
      }
    }
  }, [
    featureResolved,
    isEnabled,
    isEventSubdomain,
    eventIdPublic,
    eventSlug,
    eventIdParam,
  ]);

  if (isLoading) {
    return { shouldBlock: true };
  }

  // If feature check errored, fall through (don't block) but log for visibility
  if (error !== null) {
    applicationLogger.warn(
      "Preferred URL redirect feature check failed — falling through",
      "usePreferredUrlRedirect",
      [
        {
          eventIdPublic: EventIdPublic.unwrap(eventIdPublic),
          error: String(error),
        },
      ],
    );
    return { shouldBlock: false };
  }

  const willRedirect =
    (!isEventSubdomain && isEnabled) ||
    (isEventSubdomain &&
      !isEnabled &&
      buildBaseDomainUrl(window.location.host) !== null) ||
    (!isEventSubdomain &&
      eventSlug !== null &&
      eventIdParam !== undefined &&
      eventIdParam !== EventSlug.unwrap(eventSlug) &&
      stripEventPrefix(window.location.pathname, eventIdPublic, eventSlug) !==
        window.location.pathname);

  return { shouldBlock: willRedirect };
}
